"use client";
import Link from "next/link";
import { useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { config } from "@/config/config";
import { useRouter } from "next/navigation";
import { enqueueSnackbar } from "notistack";
import { phoneNumberRegex } from "@/utils/patterns/numberPhone";
import axios from "axios";
import { emailPatterns } from "@/utils/patterns/email";
import { formatPhoneNumber } from "@/utils/phoneNumber";
import { PasswordIcon } from "../Icons/Password";
import { PasswordViewIcon } from "../Icons/PasswordView";
import { CustomSession, loginAndRedirect } from "@/utils/authOptions";
import { passwordRegex } from "@/utils/patterns/password";
import { useSession } from "next-auth/react";
import { Role } from "@/types/Models/role";
import GooglePlacesAutocomplete, {
  geocodeByPlaceId,
  geocodeByAddress,
  getLatLng,
} from "react-google-places-autocomplete";

const FormRegisterBusinnes = ({
  categories,
  session,
  userExists,
  roles,
}: {
  categories: Array<any>;
  session: CustomSession | null;
  userExists: boolean;
  roles: Role[];
  states: Array<any>;
}) => {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: userExists ? session?.user.name : "",
      lastName: userExists ? session?.user.last_name : "",
      email: userExists ? session?.user.email : "",
      business_category_id: undefined,
      is_ecommerce: 1,
      businessName: "",
      website: "",
      password: "",
      confirm_password: "",
      phoneNumber: "",
      termsAndPrivacy: false,
    },
  });

  const isEcommerce = watch("is_ecommerce");

  const router = useRouter();
  const { data: user, update } = useSession();

  const [viewPassword, setViewPassword] = useState(false);
  const [autocomplete, setAutocomplete] = useState<any>(null);

  const [viewPasswordConfirmPassword, setViewPasswordConfirmPassword] =
    useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const formatPhone = (value: string) => {
    if (!value) return;
    const phone = formatPhoneNumber(value);
    setValue("phoneNumber", phone, { shouldDirty: true, shouldValidate: true });
  };

  const onSubmit = async (data: any) => {
    if (showLoader) return;
    setShowLoader(true);
    try {
      let latitude = undefined;
      let longitude = undefined;
      let address = undefined;

      if (data.is_ecommerce == 0 && autocomplete && autocomplete.value) {
        const { place_id, description } = autocomplete.value;
        address = description;
        if (place_id) {
          const geocode = await geocodeByPlaceId(place_id);

          if (Array.isArray(geocode) && geocode.length) {
            const { lat, lng } = await getLatLng(geocode[0]);
            latitude = lat;
            longitude = lng;
          }
        } else if (description) {
          const geocode = await geocodeByAddress(description);

          if (Array.isArray(geocode) && geocode.length) {
            const { lat, lng } = await getLatLng(geocode[0]);
            latitude = lat;
            longitude = lng;
          }
        }
      }

      const userData = {
        name: data.name,
        last_name: data.lastName,
        email: data.email,
        password: data.password,
        password_confirmation: data.confirm_password,
        business_name: data.businessName,
        business_address: address,
        open_to_sponsorship: true,
        business_category_id: data.business_category_id,
        email_address: data.email,
        phone_number: data.phoneNumber,
        website: data.website,
        is_ecommerce: data.is_ecommerce == 1 ? true : false,
        latitude,
        longitude,
        user_exists: userExists,
      };

      const userResponse = await axios.post(
        `${config.api_url}/sign-up/businesses`,
        {
          ...userData,
        },
      );

      const role = roles.find((rl) => rl.guard_name === "business_owner");

      enqueueSnackbar("Successfully created business", {
        variant: "success",
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        autoHideDuration: 2000,
        onClose: async () => {
          if (!userData.user_exists) {
            await loginAndRedirect(
              userData.email,
              userData.password,
              router.push,
            );

            return;
          }

          const available = user?.user.available_roles || [];

          const token = {
            ...user?.user,
            available_roles: [...available, role],
          };

          await update({ data: { token } });
          router.push("/user/accounts");
          return;
        },
      });
    } catch (error: any) {
      setShowLoader(false);
      setError(error.response.data.message);
      enqueueSnackbar("Error creating the organization, please try again", {
        variant: "error",
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        autoHideDuration: 2000,
      });
    }
  };

  return (
    <div className="w-full border-stroke dark:border-strokedark xl:border-l-2">
      <div className="w-full p-4 sm:py-6 sm:px-12.5">
        <div className="mb-3 text-center">
          <h2
            data-test-id="sign-up-title"
            className=" text-2xl font-bold text-black dark:text-white sm:text-title-xl2"
          >
            Business Sign up
          </h2>
          <span className="mb-1.5 block font-medium my-4">
            Advertise local and exclusive promotions to thousands of community
            members in your area
            <br />
            <span className="font-bold">FREE FOREVER.</span>
          </span>
        </div>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="grid grid-cols-1 gap-4">
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <div>
                <label className="mb-2.5 block font-medium ">First name</label>
                <div className="relative">
                  <input
                    type="text"
                    data-test-id="sign-up-business-name-surename"
                    placeholder="First name"
                    {...register("name", {
                      required: {
                        value: true,
                        message: "First name is required",
                      },
                    })}
                    className="w-full rounded-lg border border-stroke bg-white dark:bg-meta-4 dark:text-white text-black-2 py-3 pl-6 pr-10 outline-none focus:border-primary focus-visible:shadow-none dark:border-form-strokedark  dark:focus:border-primary"
                  />
                </div>
                {errors.name && (
                  <div className="text-danger p-2 text-lg">
                    <span>{errors.name.message as string}</span>
                  </div>
                )}
              </div>
              <div>
                <label className="mb-2.5 block font-medium ">Last name</label>
                <div className="relative">
                  <input
                    type="text"
                    data-test-id="sign-up-business-name-surename"
                    placeholder="Last name"
                    {...register("lastName", {
                      required: {
                        value: true,
                        message: "Last name is required",
                      },
                    })}
                    className="w-full rounded-lg border border-stroke bg-white dark:bg-meta-4 dark:text-white text-black-2 py-3 pl-6 pr-10 outline-none focus:border-primary focus-visible:shadow-none dark:border-form-strokedark dark:focus:border-primary"
                  />
                </div>
                {errors.lastName && (
                  <div className="text-danger p-2 text-lg">
                    <span>{errors.lastName.message as string}</span>
                  </div>
                )}
              </div>
              <div>
                <label className="mb-2.5 block font-medium ">Email</label>
                <div className="relative">
                  <input
                    type="email"
                    data-test-id="sign-up-business-email"
                    placeholder="Enter your email"
                    {...register("email", {
                      required: { value: true, message: "Email is required" },
                      pattern: {
                        value: emailPatterns,
                        message: "Enter a correct email address ",
                      },
                    })}
                    className="w-full rounded-lg border border-stroke bg-white dark:bg-meta-4 dark:text-white text-black-2 py-3 pl-6 pr-10 outline-none focus:border-primary focus-visible:shadow-none dark:border-form-strokedark dark:focus:border-primary"
                  />

                  <span className="absolute right-4 top-4">
                    <svg
                      className="fill-current"
                      width="22"
                      height="22"
                      viewBox="0 0 22 22"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g opacity="0.5">
                        <path
                          d="M19.2516 3.30005H2.75156C1.58281 3.30005 0.585938 4.26255 0.585938 5.46567V16.6032C0.585938 17.7719 1.54844 18.7688 2.75156 18.7688H19.2516C20.4203 18.7688 21.4172 17.8063 21.4172 16.6032V5.4313C21.4172 4.26255 20.4203 3.30005 19.2516 3.30005ZM19.2516 4.84692C19.2859 4.84692 19.3203 4.84692 19.3547 4.84692L11.0016 10.2094L2.64844 4.84692C2.68281 4.84692 2.71719 4.84692 2.75156 4.84692H19.2516ZM19.2516 17.1532H2.75156C2.40781 17.1532 2.13281 16.8782 2.13281 16.5344V6.35942L10.1766 11.5157C10.4172 11.6875 10.6922 11.7563 10.9672 11.7563C11.2422 11.7563 11.5172 11.6875 11.7578 11.5157L19.8016 6.35942V16.5688C19.8703 16.9125 19.5953 17.1532 19.2516 17.1532Z"
                          fill=""
                        />
                      </g>
                    </svg>
                  </span>
                </div>
                {errors.email && (
                  <div className="text-danger p-2 text-lg">
                    <span>{errors.email.message as string}</span>
                  </div>
                )}
              </div>

              <div>
                <label className="mb-2.5 block font-medium ">
                  Phone number
                </label>
                <div className="relative">
                  <input
                    type="text"
                    data-test-id="sign-up-business-phone-number"
                    placeholder="(201) 123-1234"
                    {...register("phoneNumber", {
                      onBlur: (e) => formatPhone(e.target.value),
                      required: {
                        value: true,
                        message: "Phone number is required",
                      },
                      pattern: {
                        value: phoneNumberRegex,
                        message: "Invalid phone number",
                      },
                    })}
                    className="w-full rounded-lg border border-stroke bg-white dark:bg-meta-4 dark:text-white text-black-2 py-3 pl-6 pr-10 outline-none focus:border-primary focus-visible:shadow-none dark:border-form-strokedark  dark:focus:border-primary"
                  />
                </div>
                {errors.phoneNumber && (
                  <div className="text-danger p-2 text-lg">
                    <span>{errors.phoneNumber.message as string}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <div>
                <label className="mb-2.5 block font-medium ">
                  Enter business name
                </label>
                <div className="relative">
                  <input
                    type="text"
                    data-test-id="sign-up-business-name"
                    placeholder="Business name"
                    {...register("businessName", {
                      required: {
                        value: true,
                        message: "Business name is required",
                      },
                    })}
                    className="w-full rounded-lg border border-stroke bg-white dark:bg-meta-4 dark:text-white text-black-2 py-3 pl-6 pr-10 outline-none focus:border-primary focus-visible:shadow-none dark:border-form-strokedark dark:focus:border-primary"
                  />
                </div>
                {errors.businessName && (
                  <div className="text-danger p-2 text-lg">
                    <span>{errors.businessName.message as string}</span>
                  </div>
                )}
              </div>

              <div>
                <label className="mb-2.5 block font-medium ">
                  Is your business primarily ecommerce?
                </label>
                <div className="relative">
                  <select
                    id="type"
                    {...register("is_ecommerce", {
                      required: {
                        value: true,
                        message: "Please indicate if you are an ecommerce",
                      },
                    })}
                    className="w-full rounded-lg border border-stroke bg-white dark:bg-meta-4 dark:text-white text-black-2 py-3 pl-6 pr-10 outline-none focus:border-primary focus-visible:shadow-none dark:border-form-strokedark dark:focus:border-primary"
                  >
                    <option value={1}>Yes</option>
                    <option value={0}>No</option>
                  </select>
                </div>
                {errors.is_ecommerce && (
                  <div className="text-danger p-2 text-lg">
                    <span>{errors.is_ecommerce.message as string}</span>
                  </div>
                )}
              </div>

              {isEcommerce == 0 && (
                <div className="flex-grow">
                  <label className="mb-2.5 block font-medium ">
                    Tell us your address
                  </label>
                  <div className="relative">
                    <GooglePlacesAutocomplete
                      apiKey={config.google_maps_key}
                      autocompletionRequest={{
                        componentRestrictions: {
                          country: "US",
                        },
                      }}
                      debounce={500}
                      selectProps={{
                        value: autocomplete,
                        onChange: setAutocomplete,
                        placeholder: "Enter your address...",
                        styles: {
                          control: (base) => {
                            const newBase = { ...base };
                            newBase.borderRadius = undefined;
                            newBase.borderColor = undefined;
                            newBase["&:hover"] = undefined;
                            newBase.boxShadow = undefined;
                            newBase.color = undefined;
                            return newBase;
                          },
                          option: (base) => {
                            const newBase = { ...base };
                            newBase["&:hover"] = undefined;
                            return newBase;
                          },
                        },
                        classNames: {
                          control: (state) =>
                            state.isFocused
                              ? "w-full rounded-lg border border-primary bg-white dark:bg-meta-4 dark:text-white text-black-2 py-1.5 pl-3 pr-2 outline-none focus:border-primary dark:border-form-strokedark dark:focus:border-primary"
                              : "w-full rounded-lg border border-stroke bg-white dark:bg-meta-4 dark:text-white text-black-2 py-1.5 pl-3 pr-2 outline-none focus:border-primary dark:border-form-strokedark dark:focus:border-primary",
                          menu: (state) =>
                            "bg-white dark:bg-meta-4 dark:text-white text-black-2",
                          input: (state) => "dark:text-white text-black-2",
                          singleValue: (state) =>
                            "dark:text-white text-black-2",
                          option: (state) =>
                            "bg-white dark:bg-meta-4 dark:text-white text-black-2 dark:hover:bg-secondary dark:hover:text-white",
                        },
                      }}
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="mb-2.5 block font-medium ">
                  Enter your website
                </label>
                <div className="relative">
                  <input
                    type="text"
                    data-test-id="sign-up-business-website"
                    placeholder="Website"
                    {...register("website", {
                      required: {
                        value: true,
                        message: "Website is required",
                      },
                      pattern: {
                        value:
                          /(\w+:?\w*)?(\S+)(:\d+)?(\/|\/([\w#!:.?+=&%!\-\/]))?/,
                        message: "Enter a valid web page",
                      },
                    })}
                    className="w-full rounded-lg border border-stroke bg-white dark:bg-meta-4 dark:text-white text-black-2 py-3 pl-6 pr-10 outline-none focus:border-primary focus-visible:shadow-none dark:border-form-strokedark dark:focus:border-primary"
                  />
                </div>
                {errors.website && (
                  <div className="text-danger p-2 text-lg">
                    <span>{errors.website.message as string}</span>
                  </div>
                )}
              </div>
              <div>
                <label className="mb-2.5 block font-medium ">
                  Select your business category
                </label>
                <div className="relative">
                  <select
                    id="type"
                    {...register("business_category_id", {
                      required: {
                        value: true,
                        message: "Business category is required",
                      },
                    })}
                    className="w-full rounded-lg border border-stroke bg-white dark:bg-meta-4 dark:text-white text-black-2 py-3 pl-6 pr-10 outline-none focus:border-primary focus-visible:shadow-none dark:border-form-strokedark dark:focus:border-primary"
                  >
                    <option value={undefined}>Your business category</option>
                    {categories.map((cat, index: number) => (
                      <option key={index} value={cat.id}>
                        {`${cat.name}`}
                      </option>
                    ))}
                  </select>
                </div>
                {errors.business_category_id && (
                  <div className="text-danger p-2 text-lg">
                    <span>{errors.business_category_id.message as string}</span>
                  </div>
                )}
              </div>

              {(!session || session?.user.provider === "credentials") && (
                <>
                  <div>
                    <label className="mb-2.5 block font-medium">
                      Create your password
                    </label>
                    <div className="relative">
                      <input
                        type={!viewPassword ? "password" : "text"}
                        data-test-id="sign-up-password"
                        placeholder="8+ Characters, 1 Capital letter and 1 Special character"
                        {...register("password", {
                          required: {
                            value: true,
                            message: "Password is required",
                          },
                          pattern: {
                            value: passwordRegex,
                            message:
                              "Password must be at least 8 characters long and include uppercase and lowercase letters, a number, and a special character.",
                          },
                        })}
                        className="w-full rounded-lg border border-stroke bg-white dark:bg-meta-4 dark:text-white text-black-2 py-3 pl-6 pr-10 outline-none focus:border-primary focus-visible:shadow-none dark:border-form-strokedark dark:focus:border-primary"
                      />

                      <span
                        className="absolute right-4 top-4 cursor-pointer"
                        onClick={() => {
                          setViewPassword(!viewPassword);
                        }}
                      >
                        {viewPassword ? (
                          <PasswordViewIcon className="w-6 h-6 opacity-50" />
                        ) : (
                          <PasswordIcon className="w-6 h-6 opacity-50" />
                        )}
                      </span>
                    </div>
                    {errors.password && (
                      <div className="text-danger p-2 text-lg">
                        <span>{errors.password.message as string}</span>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="mb-2.5 block font-medium">
                      Confirm password
                    </label>
                    <div className="relative">
                      <input
                        type={
                          !viewPasswordConfirmPassword ? "password" : "text"
                        }
                        data-test-id="sign-up-confirm-password"
                        placeholder="8+ Characters, 1 Capital letter and 1 Special character"
                        {...register("confirm_password", {
                          required: true,
                          validate: (val: string) => {
                            if (watch("password") != val) {
                              return "Your passwords do no match";
                            }
                          },
                        })}
                        className="w-full rounded-lg border border-stroke bg-white dark:bg-meta-4 dark:text-white text-black-2 py-3 pl-6 pr-10 outline-none focus:border-primary focus-visible:shadow-none dark:border-form-strokedark dark:focus:border-primary"
                      />

                      <span
                        className="absolute right-4 top-4 cursor-pointer hidden lg:block"
                        onClick={() => {
                          setViewPasswordConfirmPassword(
                            !viewPasswordConfirmPassword,
                          );
                        }}
                      >
                        {viewPasswordConfirmPassword ? (
                          <PasswordViewIcon className="w-6 h-6 opacity-50" />
                        ) : (
                          <PasswordIcon className="w-6 h-6 opacity-50" />
                        )}
                      </span>
                    </div>

                    {errors.confirm_password && (
                      <div className="text-danger p-2 text-lg">
                        <span>{errors.confirm_password.message as string}</span>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between flex-wrap">
            <div className="flex items-center my-4">
              <label className="text-sm font-medium text-gray-900 dark:text-gray-300 flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  data-test-id="sign-up-accept-terms"
                  {...register("termsAndPrivacy", {
                    required: {
                      value: true,
                      message: "Please check the box",
                    },
                  })}
                  className="w-4 h-4 me-3 text-black bg-gray-100 border-gray-300 rounded focus:ring-gray-900 dark:focus:ring-gray dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                />
                <span>
                  I agree with{" "}
                  <a
                    className="text-primary font-semibold"
                    target="_blank"
                    href="https://www.trillage.io/terms/"
                  >
                    Terms
                  </a>{" "}
                  and{" "}
                  <a
                    className="text-primary font-semibold"
                    target="_blank"
                    href="https://www.trillage.io/privacy-policy/"
                  >
                    Privacy Policy
                  </a>
                  .
                </span>
              </label>
            </div>
          </div>

          {errors.termsAndPrivacy ? (
            <div className="text-danger p-2 text-lg">
              <span>{errors.termsAndPrivacy.message as string}</span>
            </div>
          ) : (
            <></>
          )}
          {error ? (
            <div className="text-danger p-2 text-lg">
              <span>{error}</span>
            </div>
          ) : (
            <></>
          )}

          <div className="mb-5">
            <button
              type="submit"
              data-test-id="sign-up-create"
              className="flex w-full cursor-pointer text-white border-primary rounded-lg font-bold border bg-primary p-3 transition hover:bg-opacity-90 justify-center"
            >
              {showLoader ? (
                <svg
                  className="animate-spin -ml-1 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              ) : (
                <span>Sign up</span>
              )}
            </button>
          </div>
        </form>
        <div className="w-full mt-6 text-center">
          <h2 className="font-bold">
            You already have an account?{" "}
            <Link href="/sign-in" className="text-secondary">
              Log in
            </Link>
          </h2>
        </div>
      </div>
    </div>
  );
};

export default FormRegisterBusinnes;
